import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import OnboardingLayout from '../../components/OnboardingLayout'
import OnboardingField from '../../components/onboarding/OnboardingField'
import { legacyExperienceToTrainingAge } from '../../lib/calibration/onboardingConstants'
import { invalidFieldsFromChecks, scrollToFirstFieldError } from '../../lib/onboarding/fieldValidation'
import { ONBOARDING, onboardingFieldControlClass } from '../../lib/onboarding/formStyles'
import { ONBOARDING_STEP_COUNT } from '../../lib/onboarding'

import type { OnboardingStepProps } from './stepProps'

const Step1 = ({ onNext, onPrevious, initialData, stepIndex }: OnboardingStepProps) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    training_goal: (initialData?.training_goal as string) || '',
    environment: (initialData?.environment as string) || '',
    experience_level: (initialData?.experience_level as string) || '',
    training_split: (initialData?.training_split as string) || '',
  })
  const [error, setError] = useState('')
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set())

  const handleSubmit = () => {
    setError('')

    const checks = {
      training_goal: Boolean(formData.training_goal),
      environment: Boolean(formData.environment),
      experience_level: Boolean(formData.experience_level),
      training_split: Boolean(formData.training_split),
    }
    const invalid = invalidFieldsFromChecks(checks)
    if (invalid.size > 0) {
      setInvalidFields(invalid)
      setError(t('onboarding.training.selectAll'))
      scrollToFirstFieldError([...invalid])
      return
    }

    const training_age = legacyExperienceToTrainingAge(formData.experience_level)
    if (!training_age) {
      setInvalidFields(new Set(['experience_level']))
      setError(t('onboarding.training.invalidExperience'))
      scrollToFirstFieldError(['experience_level'])
      return
    }

    setInvalidFields(new Set())
    onNext({ ...formData, training_age })
  }

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setInvalidFields((prev) => {
      if (!prev.has(name)) return prev
      const next = new Set(prev)
      next.delete(name)
      return next
    })
  }

  return (
    <OnboardingLayout step={stepIndex} totalSteps={ONBOARDING_STEP_COUNT} onNext={handleSubmit} onPrev={onPrevious}>
      <form className="space-y-8">
        <div>
          <h2 className={ONBOARDING.heading}>{t('onboarding.training.heading')}</h2>
          <p className={ONBOARDING.subtitle}>{t('onboarding.training.subtitle')}</p>
        </div>

        <OnboardingField id="training_goal" label={t('onboarding.training.goal')} required invalid={invalidFields.has('training_goal')}>
          <select
            name="training_goal"
            value={formData.training_goal}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('training_goal'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="strength">{t('onboarding.training.goalStrength')}</option>
            <option value="hypertrophy">{t('onboarding.training.goalHypertrophy')}</option>
            <option value="conditioning_weight_loss">{t('onboarding.training.goalConditioning')}</option>
          </select>
        </OnboardingField>

        <OnboardingField id="environment" label={t('onboarding.training.environment')} required invalid={invalidFields.has('environment')}>
          <select
            name="environment"
            value={formData.environment}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('environment'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="gym">{t('onboarding.training.envGym')}</option>
            <option value="home">{t('onboarding.training.envHome')}</option>
          </select>
        </OnboardingField>

        <OnboardingField id="experience_level" label={t('onboarding.training.experience')} required invalid={invalidFields.has('experience_level')}>
          <select
            name="experience_level"
            value={formData.experience_level}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('experience_level'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="beginner">{t('onboarding.training.expBeginner')}</option>
            <option value="regular">{t('onboarding.training.expRegular')}</option>
            <option value="advanced">{t('onboarding.training.expAdvanced')}</option>
          </select>
        </OnboardingField>

        <OnboardingField id="training_split" label={t('onboarding.training.split')} required invalid={invalidFields.has('training_split')}>
          <select
            name="training_split"
            value={formData.training_split}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('training_split'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="full_body">{t('onboarding.training.splitFullBody')}</option>
            <option value="upper_lower">{t('onboarding.training.splitUpperLower')}</option>
            <option value="ppl">{t('onboarding.training.splitPpl')}</option>
            <option value="body_part">{t('onboarding.training.splitBodyPart')}</option>
            <option value="free_text">{t('onboarding.training.splitFreeText')}</option>
          </select>
        </OnboardingField>

        {error ? <div className={ONBOARDING.error}>{error}</div> : null}
      </form>
    </OnboardingLayout>
  )
}

export default Step1
